import { CalendarDays, UsersRound } from "lucide-react";
import { cn } from "@/lib/utils";

/** Stable per-person colour, so the same person looks the same everywhere. */
const AVATAR_COLOURS = [
    "bg-sky-600", "bg-violet-600", "bg-emerald-600", "bg-amber-600",
    "bg-rose-600", "bg-teal-600", "bg-indigo-600", "bg-fuchsia-600"
];

export function colourFor(name: string): string {
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
    return AVATAR_COLOURS[Math.abs(hash) % AVATAR_COLOURS.length]!;
}

export function initials(name: string): string {
    const words = name.replace(/\(guest\)/i, "").split(/[\s@._-]+/).filter(Boolean);
    if (words.length === 0) return "?";
    if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
    return (words[0]![0]! + words[words.length - 1]![0]!).toUpperCase();
}

/** A person (initials in their colour, with a presence dot), a group, or a meeting's chat. */
export function ChatAvatar({ name, group, meeting, presence, size = "md" }: {
    name: string;
    group?: boolean;
    meeting?: boolean;
    presence?: string;
    /** sm 32px (messages, header) · list 36px (chat list rows) · md 40px. */
    size?: "sm" | "list" | "md";
}) {
    const box = size === "sm" ? "h-8 w-8 text-[11px]" : size === "list" ? "h-9 w-9 text-xs" : "h-10 w-10 text-xs";
    const icon = size === "md" ? "h-5 w-5" : "h-4 w-4";
    return (
        <span className={cn("relative flex shrink-0 items-center justify-center rounded-full font-semibold text-white", box, meeting ? "bg-primary" : group ? "bg-slate-500" : colourFor(name))} aria-hidden>
            {meeting ? <CalendarDays className={icon} /> : group ? <UsersRound className={icon} /> : initials(name)}
            {presence ? (
                <span className={cn("absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-card", presenceDotClass(presence))} />
            ) : null}
        </span>
    );
}

/** Presence dot fill — the mock-up's Teams colours; offline is a hollow ring. */
export function presenceDotClass(presence: string): string {
    switch (presence) {
        case "online": return "bg-[#13a10e]";
        case "away":
        case "brb": return "bg-[#eaa300]";
        case "busy":
        case "dnd": return "bg-[#d13438]";
        default: return "bg-card shadow-[inset_0_0_0_1.5px_var(--connect-text-3)]";
    }
}

/** Colour for a presence label ("Available" in green, as in the chat header). */
export function presenceTextClass(presence: string | undefined): string {
    switch (presence) {
        case "online": return "text-[var(--connect-ok)]";
        case "away":
        case "brb": return "text-[var(--connect-away)]";
        case "busy":
        case "dnd": return "text-[var(--connect-busy)]";
        default: return "text-[var(--connect-text-3)]";
    }
}

export function presenceLabel(presence: string | undefined): string {
    switch (presence) {
        case "online": return "Available";
        case "away": return "Away";
        case "brb": return "Be right back";
        case "busy": return "Busy";
        case "dnd": return "Do not disturb";
        default: return "Offline";
    }
}

export function sameDay(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/** Chat list time: "3:45 PM" today, "Yesterday", weekday this week, else "12 Sep". */
export function listTime(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const now = new Date();
    if (sameDay(d, now)) return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (sameDay(d, yesterday)) return "Yesterday";
    if (now.getTime() - d.getTime() < 6 * 86_400_000) return d.toLocaleDateString(undefined, { weekday: "short" });
    return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

/** Separator between days in a conversation. */
export function dayLabel(d: Date): string {
    const now = new Date();
    if (sameDay(d, now)) return "Today";
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (sameDay(d, yesterday)) return "Yesterday";
    return d.toLocaleDateString(undefined, {
        weekday: "long", day: "numeric", month: "long",
        ...(d.getFullYear() !== now.getFullYear() ? { year: "numeric" } : {})
    });
}

export function timeLabel(d: Date): string {
    return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

export function typingText(names: string[]): string | null {
    if (names.length === 0) return null;
    if (names.length === 1) return `${names[0]} is typing…`;
    if (names.length === 2) return `${names[0]} and ${names[1]} are typing…`;
    return "Several people are typing…";
}
